/**
 * Quiz Data Handler for Pupsday
 * Handles quiz data from external onboarding flow
 * Stores data in localStorage and passes to Shopify
 */

class QuizDataHandler {
  constructor() {
    this.quizData = {};
    this.init();
  }

  init() {
    // Check for quiz data in URL parameters
    this.parseURLParams();
    
    // Store quiz data in localStorage for persistence
    this.storeQuizData();
    
    // Apply quiz filters to products
    this.applyFilters();
    
    // Show quiz-based recommendations
    this.showRecommendations();
  }

  parseURLParams() {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Quiz parameters we expect from onboarding.pupsday.com
    this.quizData = {
      dogSize: urlParams.get('dog_size') || null, // small, medium, large, xlarge
      breed: urlParams.get('breed') || null,
      preferences: urlParams.get('preferences') || null, // organic, grain-free, etc.
      treatType: urlParams.get('treat_type') || null, // chewy, crunchy, soft
      subscription: urlParams.get('subscription') || null, // monthly, biweekly, weekly
      productType: urlParams.get('product_type') || null, // treats, accessories, both
      dogAge: urlParams.get('dog_age') || null, // puppy, adult, senior
      allergies: urlParams.get('allergies') || null, // comma-separated list
      activityLevel: urlParams.get('activity_level') || null // low, medium, high
    };

    // Clean up null values
    Object.keys(this.quizData).forEach(key => {
      if (this.quizData[key] === null) {
        delete this.quizData[key];
      }
    });
  }

  storeQuizData() {
    if (Object.keys(this.quizData).length > 0) {
      localStorage.setItem('pupsday_quiz_data', JSON.stringify(this.quizData));
      localStorage.setItem('pupsday_quiz_timestamp', Date.now().toString());
    } else {
      // Try to load from localStorage if URL params not present
      const stored = localStorage.getItem('pupsday_quiz_data');
      if (stored) {
        this.quizData = JSON.parse(stored);
      }
    }
  }

  getQuizData() {
    return this.quizData;
  }

  getSubscriptionPreference() {
    return this.quizData.subscription || 'monthly';
  }

  getProductFilters() {
    const filters = {};
    
    if (this.quizData.dogSize) {
      filters.dog_size = this.quizData.dogSize;
    }
    
    if (this.quizData.productType) {
      filters.product_type = this.quizData.productType;
    }
    
    if (this.quizData.preferences) {
      filters.preferences = this.quizData.preferences;
    }
    
    if (this.quizData.treatType) {
      filters.treat_type = this.quizData.treatType;
    }
    
    return filters;
  }

  applyFilters() {
    // This will be used to filter products on collection pages
    const filters = this.getProductFilters();
    
    // Store filters for use in Liquid templates
    if (typeof window !== 'undefined') {
      window.pupsdayQuizFilters = filters;
    }
    
    // Apply filters to current page if on collection page
    if (window.location.pathname.includes('/collections/')) {
      this.filterCollectionProducts(filters);
    }
  }

  filterCollectionProducts(filters) {
    // Filter products based on quiz data
    const products = document.querySelectorAll('[data-product-handle]');
    
    products.forEach(product => {
      const handle = product.getAttribute('data-product-handle');
      const productTags = product.getAttribute('data-product-tags') || '';
      
      let shouldShow = true;
      
      // Filter by dog size
      if (filters.dog_size) {
        const sizeTags = ['small', 'medium', 'large', 'xlarge'];
        const hasSizeTag = sizeTags.some(size => 
          productTags.toLowerCase().includes(`dog-size-${filters.dog_size}`)
        );
        if (!hasSizeTag && filters.dog_size) {
          // Only hide if product has specific size tags and doesn't match
          const productHasAnySize = sizeTags.some(size => 
            productTags.toLowerCase().includes(`dog-size-${size}`)
          );
          if (productHasAnySize) {
            shouldShow = false;
          }
        }
      }
      
      // Filter by product type
      if (filters.product_type && filters.product_type !== 'both') {
        const hasTypeTag = productTags.toLowerCase().includes(filters.product_type);
        if (!hasTypeTag) {
          shouldShow = false;
        }
      }
      
      // Show/hide product
      if (shouldShow) {
        product.style.display = '';
      } else {
        product.style.display = 'none';
      }
    });
  }

  showRecommendations() {
    // Show personalized recommendations banner
    if (Object.keys(this.quizData).length > 0) {
      this.createRecommendationBanner();
    }
  }

  createRecommendationBanner() {
    // Check if banner already exists
    if (document.getElementById('quiz-recommendations-banner')) {
      return;
    }

    const banner = document.createElement('div');
    banner.id = 'quiz-recommendations-banner';
    banner.className = 'quiz-recommendations-banner';
    
    const subscription = this.getSubscriptionPreference();
    const dogSize = this.quizData.dogSize || 'your dog';
    
    banner.innerHTML = `
      <div class="quiz-banner-content">
        <span class="quiz-banner-icon">🐾</span>
        <div class="quiz-banner-text">
          <strong>Personalized for ${dogSize} dogs!</strong>
          Based on your quiz, we've filtered products just for you.
          ${subscription ? `<span class="quiz-subscription">Recommended: ${subscription} subscription</span>` : ''}
        </div>
        <button class="quiz-banner-close" onclick="this.parentElement.parentElement.remove()">×</button>
      </div>
    `;
    
    // Insert at top of main content
    const mainContent = document.querySelector('main') || document.body;
    mainContent.insertBefore(banner, mainContent.firstChild);
  }

  // Method to send quiz data to Shopify Customer API (requires authentication)
  async saveToCustomerMetafields(customerId) {
    // This would require Shopify Customer API access
    // For now, we'll use localStorage and URL params
    console.log('Quiz data saved locally:', this.quizData);
  }

  // Get recommended subscription frequency
  getRecommendedSubscription() {
    const activity = this.quizData.activityLevel;
    
    if (activity === 'high') {
      return 'biweekly'; // More active dogs need more frequent treats
    } else if (activity === 'low') {
      return 'monthly'; // Less active dogs need fewer treats
    }
    
    return this.quizData.subscription || 'monthly';
  }

  // Get recommended product collection based on quiz
  getRecommendedCollection() {
    if (this.quizData.productType === 'treats') {
      return '/collections/dog-treats';
    } else if (this.quizData.productType === 'accessories') {
      return '/collections/accessories';
    }
    return '/shop';
  }
}

// Initialize on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.pupsdayQuiz = new QuizDataHandler();
  });
} else {
  window.pupsdayQuiz = new QuizDataHandler();
}

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = QuizDataHandler;
}


